/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useServiceContext } from '../../root'
import Modal from '../../components/Modal'
import { useDeleteNotification } from '../../hooks/useNotifications'
import { toast } from '../../services/toast'
import _ from 'lodash'

interface NotificationFilter {
  webSocketHistoryType: string[]
  tenantUuid?: string
}

interface NotificationItem {
  id: number
  seen: boolean
  [key: string]: any
}

interface DeleteNotificationProps {
  notificationIds: number | number[]
  isWholeResultSelection?: boolean
  notification?: NotificationItem
  filter?: NotificationFilter
  onClose?: () => void
}

const DeleteNotification: React.FC<DeleteNotificationProps> = ({
  notificationIds,
  isWholeResultSelection,
  notification,
  filter,
  onClose
}) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(true)
  const { mutate: deleteNotification } = useDeleteNotification()

  const doClose = () => {
    setIsOpen(false)
    if (_.isFunction(onClose)) {
      onClose()
    }
  }

  const removeNotification = () => {
    deleteNotification(
      {
        notificationIds,
        isWholeResultSelection,
        filter
      },
      {
        onSuccess: () => {
          doClose()
          window.dispatchEvent(new CustomEvent('updateNotificationsCount'))
          if (_.isArray(notificationIds) || notification?.seen) {
            window.dispatchEvent(new CustomEvent('updateNotifications'))
          }
          window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
          // Force refetch of all notification-related queries
          queryClient.invalidateQueries({ queryKey: ['notifications-list'] })
          queryClient.invalidateQueries({ queryKey: ['notification-count'] })
          queryClient.invalidateQueries({ queryKey: ['ws-history-types'] })
        },
        onError: (error: any) => {
          toast.error(error?.data?.message || translate('NOTIFICATIONS_DELETE_ERROR'))
        }
      }
    )
  }

  return (
    <Modal show={isOpen} handleClose={doClose} title={translate('NOTIFICATIONS_DELETE_TITLE')} size="md">
      <Modal.Body>
        <div dangerouslySetInnerHTML={{ __html: translate('NOTIFICATIONS_DELETE_CONFIRMATION_QUESTION') }}></div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="secondary" onClick={doClose} data-action="ok">
          {translate('NO')}
        </Modal.Footer.Button>
        <Modal.Footer.Button
          variant="primary"
          onClick={removeNotification}
          data-action="ok"
          // disabled={isPending}
        >
          {translate('YES')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DeleteNotification
